import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild
} from '@angular/core';
import { ApiError, AuthService } from 'app/core';
import { EnumUsuarioEscopo } from 'app/domains/core/usuario/enums/usuario-escopo.enum';
import { IUsuarioFilter } from 'app/domains/core/usuario/interface/usuario-filter.interface';
import { UsuarioApi } from 'app/domains/core/usuario/apis/usuario.api';
import { EnumUsuarioPerfil } from './enums/usuario-perfil.enum';
import { IUsuarioResponse } from 'app/domains/core/usuario/interface/usuario.interface';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { IOption } from '../multi-select/models/option.interface';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastService } from '../toast/services/toast.service';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { EnumSituacao } from '../../enums/situacao.enum';

@Component({
  selector: 'sgt-usuario-multi-select',
  templateUrl: './usuario-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UsuarioMultiSelectComponent),
      multi: true
    }
  ]
})
export class UsuarioMultiSelectComponent
  extends CustomControlValueAccessor<number[]>
  implements OnInit {
  constructor(
    private usuarioApi: UsuarioApi,
    private toastService: ToastService,
    private authService: AuthService
  ) {
    super();
  }

  // Controle interno de estado
  private loading = false;
  private loaded = false;

  private defaultSettings: IMultiSelectSettings = {
    placeholder: 'Selecione os usuários',
    singular: 'Usuário',
    plural: 'Usuários',
    searchEmptyResultMessage: 'Nenhum usuário encontrado',
    showFooter: true
  };

  @Input() settings: IMultiSelectSettings;
  @Input() situacao?: EnumSituacao = EnumSituacao.ATIVO;
  @Input() perfil: EnumUsuarioPerfil;
  @Input() escopo?: EnumUsuarioEscopo = EnumUsuarioEscopo.VINCULO;
  @Input() variante: 'filtro' | undefined;
  @Input() icon: string;

  get iconePorPerfil(): string {
    if (this.icon) {
      return this.icon;
    }

    switch (this.perfil) {
      case EnumUsuarioPerfil.ATENDENTE:
        return 'fa-headset';
      case EnumUsuarioPerfil.GERENTE:
        return 'fa-user-shield';
      case EnumUsuarioPerfil.REPRESENTANTE:
        return 'fa-user-tie';
      default:
        return 'fa-users';
    }
  }

  @Output() changed = new EventEmitter<number[]>();
  @Output() search = new EventEmitter<boolean>();
  @ViewChild('multiSelectRef')
  multiSelectComponent: MultiSelectComponent;

  selectorOptions: IOption[] = [];

  ngOnInit(): void {
    this.settings = {
      ...this.defaultSettings,
      ...this.settings
    };
  }

  buildFilter(): IUsuarioFilter {
    const filter: IUsuarioFilter = {} as IUsuarioFilter;

    if (this.perfil == EnumUsuarioPerfil.ATENDENTE) {
      filter.perfil = EnumUsuarioPerfil.ATENDENTE;
    }

    if (this.perfil == EnumUsuarioPerfil.GERENTE) {
      filter.perfil = EnumUsuarioPerfil.GERENTE;
    }

    if (this.situacao != null) {
      filter.situacao = this.situacao;
    }

    if (this.escopo != null) {
      filter.escopo = this.escopo;
    }

    return filter;
  }

  onFirstOpened(isFirstOpened: boolean): void {
    if (isFirstOpened) {
      this.loadIfNeeded();
    }
  }

  // Chamado quando o FormControl escreve valor
  protected updateValue(value: number[] | null): void {
    const ids = value ?? [];
    if (ids.length > 0 && this.selectorOptions.length === 0) {
      this.loadIfNeeded();
      return;
    }
    this.updateCheckedInternal(ids);
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = {
      ...this.settings,
      isDisabled
    };
  }

  // Carrega opções somente uma vez.
  // Aplica seleção após o load.
  private loadIfNeeded(): void {
    if (this.loaded || this.loading || this.isMesmoPerfilDoLogado()) {
      return;
    }

    this.definirCarregando(true);

    const filter = this.buildFilter();
    this.usuarioApi.getUsuarios(filter).subscribe({
      next: (response) => {
        this.buildSelectOptions(response.data);
        this.syncMultiSelect();

        this.loaded = true;
        this.definirCarregando(false);

        // Aplica seleção pendente
        if (this.value?.length) {
          this.updateCheckedInternal(this.value);
        }
      },
      error: (err: ApiError) => {
        this.definirCarregando(false);
        this.toastService.error(err.message, {
          title: err.cause || 'Aconteceu algo inesperado.',
          duration: 5000
        });
      }
    });
  }

  // Atualiza o estado checked com base nos IDs selecionados
  private updateCheckedInternal(selectedIds: number[]): void {
    const ids = selectedIds ?? [];
    this.selectorOptions.forEach((option) => {
      option.isChecked = ids.includes(option.id);
    });

    this.syncMultiSelect();
  }

  // Sincroniza manualmente com o componente filho
  private syncMultiSelect(): void {
    if (!this.multiSelectComponent) {
      return;
    }

    this.multiSelectComponent.options = [...this.selectorOptions];
    this.multiSelectComponent.filteredOptions = [...this.selectorOptions];
    this.multiSelectComponent.totalSelectedOptions();
    this.multiSelectComponent.changePlaceholder();
  }

  // Disparado quando o usuário altera a seleção manualmente
  onSelectionChange(): void {
    const selected = this.selectorOptions
      .filter((opt) => opt.isChecked)
      .map((opt) => opt.id);

    this.emitChange(selected);
    this.changed.emit(selected);
    this.markAsTouched();
  }

  // Constrói as opções para o seletor
  private buildSelectOptions(list: IUsuarioResponse[]): void {
    const currentValue = this.value || [];
    const listData = list ?? [];

    this.selectorOptions = listData.map((info) => ({
      id: info.id,
      label: info.nome,
      isChecked: currentValue.includes(info.id)
    }));
  }

  // Filtrar pelo próprio perfil não estreita o resultado, então a lista não é oferecida
  private isMesmoPerfilDoLogado(): boolean {
    return this.perfil === this.authService.getIdPerfilUsuario();
  }

  private definirCarregando(carregando: boolean): void {
    this.loading = carregando;
    this.settings = { ...this.settings, isLoading: carregando };
  }
}
